import React, { createContext, useContext, useEffect } from 'react';
import style from './styles.module.css';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { Header } from '@components/SettingsLayout';
import { Text, Icon, IconButton, IconType } from '@/ui';

type ModalContextType = {
  closeModal: () => void;
  open: boolean;
};
type ModalContentProps = {
  children: React.ReactNode;
  border?: boolean;
};
type ModalFooterProps = ModalContentProps & {
  justify?: 'center' | 'space-between' | 'flex-end';
};

type ModalTitleProps = {
  title: string;
  border?: boolean;
  children?: React.ReactNode;
};
const ModalContext = createContext<ModalContextType>({
  closeModal: () => {},
  open: false,
});

type Props = {
  closeModal: () => void;
  open: boolean;
  children: React.ReactNode;
  isLarge?: boolean;
  isMedium?: boolean;
  fitContent?: boolean;
  backGround?: 'transparent' | 'white';
  closeOnOverlayClick?: boolean;
};

const Modal: React.FC<Props> & {
  Title: React.FC<ModalTitleProps>;
  Content: React.FC<ModalContentProps>;
  Footer: React.FC<ModalFooterProps>;
} = ({
  closeModal,
  open,
  children,
  isLarge = false,
  isMedium = false,
  fitContent = false,
  backGround = 'white',
  closeOnOverlayClick = true,
}) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  useEffect(() => {
    if (!closeOnOverlayClick) {
      return;
    }

    const handleClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).classList.contains(style.overlay)) {
        closeModal();
      }
    };
    document.addEventListener('mousedown', handleClick);

    return () => document.removeEventListener('mousedown', handleClick);
  }, [closeOnOverlayClick, closeModal]);

  if (!open) {
    return null;
  }
  const windowStyles = classNames(style.window, {
    [style.isLarge]: isLarge,
    [style.isMedium]: isMedium,
    [style.fitContent]: fitContent,
    [style.transparent]: backGround === 'transparent',
  });
  return createPortal(
    <ModalContext.Provider value={{ closeModal, open }}>
      <div className={style.overlay}>
        <div className={windowStyles}>{children}</div>
      </div>
    </ModalContext.Provider>,
    document.body,
  );
};

const Title: React.FC<ModalTitleProps> = ({ title, border = true, children }) => {
  const { closeModal } = useContext(ModalContext);
  const titleClass = classNames(style.title, {
    [style.border]: border,
  });
  return (
    <div className={titleClass}>
      <Header>
        <Text size='l' weight='medium'>
          {title}
        </Text>
        <IconButton onClick={closeModal}>
          <Icon type={IconType.Close} width={16} height={16} fill='currentColor' />
        </IconButton>
      </Header>
      {children}
    </div>
  );
};

const Content: React.FC<ModalContentProps> = ({ children, border = false }) => {
  const contentStyles = classNames(style.content, {
    [style.border]: border,
  });
  return <div className={contentStyles}>{children}</div>;
};

const Footer: React.FC<ModalFooterProps> = ({ children, justify = 'center' }) => {
  const footerStyles = classNames(style.footer, {
    [style.justifyCenter]: justify === 'center',
    [style.justifySpaceBetween]: justify === 'space-between',
    [style.justifyFlexEnd]: justify === 'flex-end',
  });
  return <div className={footerStyles}>{children}</div>;
};

Modal.Title = Title;
Modal.Content = Content;
Modal.Footer = Footer;
export { Modal };
